<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitButton"
    @close="handleClose"
    @reset="handleReset"
    @submit.prevent="confirmSubmission"
  >
    <template #header-info>
      <PBadge
        v-if="formData._id"
        :label="computedStatus.label"
        :type="computedStatus.type"
        class="q-mx-md"
      />
      <PTooltip v-if="toolTipContent"> {{ toolTipContent }} </PTooltip>
      <PBtn v-if="formData._id" flat dense>
        <MenuIcon class="icon" />
        <q-menu
          :offset="[30, 8]"
          anchor="bottom left"
          self="top middle"
          fit
          :style="{ width: '120px', borderRadius: '4px' }"
        >
          <q-list>
            <q-item
              v-for="({ label, onClick }, index) in dealSettingMenuItems"
              :key="index"
              v-close-popup
              clickable
              dense
              @click="onClick"
            >
              <q-item-section no-wrap>
                <q-item-label
                  :title="label"
                  class="text-primary text-xs text-weight-medium flex items-center"
                  lines="1"
                >
                  {{ label }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </PBtn>
    </template>

    <div class="row q-col-gutter-md q-mb-xl">
      <div class="col-6">
        <p class="q-mb-xxs text-neutral-70">Customer</p>
        <p class="q-mb-none">{{ buyer.shop_name }}</p>
        <p class="q-mb-none">{{ buyer.gstin }}, {{ buyer.state_cd }}</p>
      </div>
      <div class="col-6">
        <p class="q-mb-xxs text-neutral-70">Vendor</p>
        <p class="q-mb-none">{{ seller.shop_name }}</p>
        <p class="q-mb-none">{{ seller.gstin }}, {{ seller.state_cd }}</p>
      </div>
    </div>

    <PSelectInput
      v-model="formData.deal_lead"
      class="q-mb-sm"
      :options="dealLeadOptions"
      help="Customer / Vendor"
      label="Who is leading this deal?"
      label-position="side"
      placeholder="Select lead"
      required
    >
    </PSelectInput>

    <fieldset>
      <legend class="q-mb-md">Invoice Settings</legend>

      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PInput
            v-model.number="formData.net_sales_margin_percent"
            :validations="{
              required: true,
              gte: 0,
              lte: 100,
              type: 'float',
            }"
            class="prepend-bg"
            help="Excluding GST Liability"
            label="Net Trade Margin"
            label-position="side"
            max="100"
            min="0"
            placeholder="0.00"
            step="any"
            type="number"
            @update:model-value="
              (value) => updateFormData(value, 'net_sales_margin_percent')
            "
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>

        <div class="col-12">
          <PInput
            v-model.number="formData.virtual_credit_cycle"
            min="1"
            :validations="{
              gte: 1,
              lte: 120,
              required: true,
              type: 'number',
            }"
            help="ToT days"
            label="Virtual Credit Days"
            label-position="side"
            max="120"
            placeholder="Enter virtual credit days"
            type="number"
            @update:model-value="
              (value) => updateFormData(value, 'virtual_credit_cycle')
            "
          >
          </PInput>
        </div>

        <div class="col-12">
          <PSelectInput
            v-model="formData.virtual_credit_start_date"
            :options="virtualCreditStartDateOptions"
            help="To calculate invoice due date"
            label="Virtual Credit Start Date"
            label-position="side"
            placeholder="Select option"
            required
          >
          </PSelectInput>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <legend class="flex item-center justify-between">
        <span>Vendor Payments Spread</span>

        <span class="text-sm"> {{ totalPaymentDistributionPercent }}% </span>

        <q-linear-progress
          :value="totalPaymentDistributionPercent / 100"
          :color="
            totalPaymentDistributionPercent / 100 === 1
              ? 'success-70'
              : 'primary'
          "
          rounded
          class="q-mt-sm"
        />
      </legend>

      <p v-if="totalPaymentDistributionPercent < 100" class="text-error-70">
        Vendor Payments Spread should be 100%
      </p>

      <p>👉 Hold-backs! - Hold percentage of bill amount.</p>

      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PInput
            v-model.number="formData.payment_distribution.margin_money"
            :max="getMaxFor('margin_money')"
            :validations="{
              required: true,
              gte: 0,
              lte: getMaxFor('margin_money'),
              type: 'float',
            }"
            class="prepend-bg"
            label="Margin Money Percentage"
            min="0"
            placeholder="0.00"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>

          <div class="flex">
            <span class="inline-block q-mr-md">Hold GST Money?</span>
            <POptionGroup
              v-model="formData.is_gst_amount_hold"
              :options="[
                {
                  label: 'Yes',
                  value: true,
                },
                {
                  label: 'No',
                  value: false,
                },
              ]"
              @update:model-value="onUpdateGstAmountHold"
            />
          </div>
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.margin_money_provider"
            :options="marginMoneyProviderOptions"
            label="Margin Money Payer"
            placeholder="Select payer"
            required
            @update:model-value="onUpdateMarginMoneyProvider"
          >
          </PSelectInput>

          <PInput
            v-model.number="formData.payment_distribution.gst_hold"
            :disable="!formData.is_gst_amount_hold"
            :help="`MAX ${getMaxFor('gst_hold')}%`"
            :max="getMaxFor('gst_hold')"
            :validations="{
              required: true,
              gte: 0,
              lte: getMaxFor('gst_hold'),
              type: 'float',
            }"
            class="prepend-bg"
            label="GST Percentage"
            label-position="side"
            min="0"
            placeholder="Enter percent"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>

        <div class="col-4">
          <PInput
            v-model.number="formData.payment_distribution.payment_received"
            :help="`MAX ${getMaxFor('payment_received')}%`"
            :max="getMaxFor('payment_received')"
            :validations="{
              required: true,
              gte: 0,
              lte: getMaxFor('payment_received'),
              type: 'float',
            }"
            class="prepend-bg"
            label="Post Advance Paid"
            min="0"
            placeholder="Enter percent"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>

        <div class="col-4">
          <PInput
            v-model.number="formData.payment_distribution.grn"
            :help="`MAX ${getMaxFor('grn')}%`"
            :max="getMaxFor('grn')"
            :validations="{
              required: true,
              gte: 0,
              lte: getMaxFor('grn'),
              type: 'float',
            }"
            class="prepend-bg"
            label="Post GRN"
            min="0"
            placeholder="Enter percent"
            step="any"
            type="number"
            @update:model-value="resetPostGrnDays"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>

        <div class="col-4">
          <PInput
            v-model.number="formData.payment_distribution.quality_check"
            :help="`MAX ${getMaxFor('quality_check')}%`"
            :max="getMaxFor('quality_check')"
            :validations="{
              required: true,
              gte: 0,
              lte: getMaxFor('quality_check'),
              type: 'float',
            }"
            class="prepend-bg"
            label="Post Quality Check"
            min="0"
            placeholder="Enter percent"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>

        <div class="col-6">
          <PInput
            v-model.number="formData.grn_payment_delay_days"
            help="Days after GRN to release payment"
            :disable="!formData.payment_distribution.grn"
            :validations="{
              required: true,
              gte: 0,
              type: 'integer',
            }"
            class="prepend-bg"
            label="Additional Days Post GRN"
            min="0"
            placeholder="Enter additional days post GRN"
            :step="1"
            type="number"
            @update:model-value="
              (value) => updateFormData(value, 'grn_payment_delay_days')
            "
          />
        </div>
      </div>
    </fieldset>

    <fieldset>
      <legend>Delay Handling</legend>

      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PSelectInput
            v-model="formData.delay_attribution"
            :options="delayAttributionOptions"
            label="Delay Attributed To"
            placeholder="Select option"
            required
          >
          </PSelectInput>

          <div class="flex">
            <span class="inline-block q-mr-md">Pro-rata on delays?</span>
            <POptionGroup
              v-model="formData.is_prorata_enabled"
              :options="[
                {
                  label: 'Yes',
                  value: true,
                },
                {
                  label: 'No',
                  value: false,
                },
              ]"
            />
          </div>
        </div>

        <div class="col-6">
          <PInput
            v-model.number="formData.delay_interest_percent"
            :validations="{
              required: true,
              gte: getMinDelayInterest(),
              lte: 100,
              type: 'float',
            }"
            class="prepend-bg"
            label="Delay Interest (per day)"
            min="0"
            max="100"
            :error="delayInterestProps.error"
            :error-message="delayInterestProps.errorMessage"
            placeholder="Enter percent"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>
        <div class="col-12">
          <PInput
            v-if="!isFetchingMinRocePercentage"
            v-model.number="formData.roce_percentage"
            :help="`Minimum ROCE required is ${minRocePercentage}%`"
            :disable="true"
            :error="isRoceBelowMin"
            :error-message="errorMsgForRoceField"
            :validations="{
              required: true,
              gte: minRocePercentage,
              lte: 100,
              type: 'float',
            }"
            class="prepend-bg"
            label="ROCE"
            label-position="side"
            placeholder="Enter percent"
            step="any"
            type="number"
          >
            <template #prepend>
              <span class="text-sm">%</span>
            </template>
          </PInput>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <legend>Associate Agent</legend>

      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PSelectInput
            v-model="formData.agent"
            :emit-value="false"
            :map-options="false"
            :options="unref(agentFilter.entities)"
            :options-loading="unref(agentFilter.loading)"
            help="Agent / Broker"
            label="Select an Agent"
            option-label="name"
            option-value="_id"
            placeholder="Select agent"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="agentFilter.onScroll"
            @filter="agentFilter.onFilter"
            @filter-abort="agentFilter.onFilterAbort"
            @update:model-value="onUpdateAgent"
          >
          </PSelectInput>
        </div>

        <div class="col-6">
          <PSelectInput
            v-model="formData.agent.commission_type"
            :fill-input="false"
            :disable="areAgentAssociatedFieldsDisabled"
            :options="agentCommissionTypeOptions"
            :use-input="false"
            label="Agent's Commission Type"
            placeholder="Select option"
            required
            @update:model-value="onUpdateAgentCommissionType"
          >
          </PSelectInput>
        </div>

        <div class="col-6">
          <PInput
            v-model.number="
              formData.agent[`commission_${formData.agent.commission_type}`]
            "
            :max="maxCommissionValue"
            :validations="{
              gte: 0,
              lte: maxCommissionValue,
              type: 'float',
            }"
            :disable="areAgentAssociatedFieldsDisabled"
            class="prepend-bg"
            label="Agent's Commission"
            min="0"
            :placeholder="
              formData.agent.commission_type === 'percent'
                ? 'Enter percent'
                : 'Enter rate'
            "
            step="any"
            type="number"
            @update:model-value="
              (value) =>
                updateFormData(
                  value,
                  `agent.commission_${formData.agent.commission_type}`,
                )
            "
          >
            <template #prepend>
              <span class="text-sm">
                {{ formData.agent.commission_type === "percent" ? "%" : "₹" }}
              </span>
            </template>
          </PInput>
        </div>
      </div>
    </fieldset>
    <q-separator v-if="isDealSettingApprovalEnabled" class="q-mb-xl q-mt-md" />
    <div
      v-if="isDealSettingApprovalEnabled"
      class="border-none q-ma-none q-pa-none"
    >
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PFile
            v-if="showFileUpload"
            v-model="file"
            :required="!formData?.files?.length"
            label="Attach Proof of Document (Deal Setting)"
            accept=".pdf, .png, .jpeg, .xlsx, .csv"
            help="Allowed file types are PDFs, images, excel, csv"
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
          <FilesList
            v-else-if="unref(formData)?.files"
            :files="formData?.files"
            :removal-allowed="false"
            file-type="Deal Terms"
          />
        </div>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar, is } from "quasar";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import dealSettingFormModel from "src/modules/deal/models/dealSettingForm";

import MenuIcon from "assets/icons/menu.svg";

import PConfirmDialog from "src/components/PConfirmDialog.vue";

import { notify } from "assets/scripts/utilities/api-clients";
import { omitKeys, setProperty } from "src/assets/scripts/utilities/helpers";

const statusLabel = {
  true: "Enabled",
  false: "Disabled",
  undefined: "Enabled",
};

const labelType = {
  true: "enabled",
  false: "deactivated",
  undefined: "enabled",
};

const confirmationStatus = {
  true: "enable",
  false: "disable",
};

const props = defineProps({
  buyer: {
    type: Object,
    default: () => ({}),
  },
  seller: {
    type: Object,
    default: () => ({}),
  },
});

const currentStatus = {
  true: true,
  undefined: true,
  false: false,
};

const $q = useQuasar();

const handleStatusUpdateConfirmation = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to ${confirmationStatus[!unref(formData).is_active]} this deal setting between ${props.buyer.shop_name} & ${props.seller.shop_name}?`,
      isFooterVisible: true,
      submitLabel: "Yes",
      submittable: true,
      cancelLabel: "No",
      cancellable: true,
      closable: false,
      onConfirm: () => {
        handleUpdateStatus();
      },
    },
  });
};

const handleUpdateStatus = async () => {
  await putDealSettingStatus({
    _id: unref(dealSetting)._id,
    is_active: currentStatus[unref(formData).is_active],
    buyer_name: props.buyer.name,
    seller_name: props.seller.name,
  });

  await closeForm();
};

const dealLeadOptions = [
  {
    label: "Vendor",
    value: "seller",
  },
  {
    label: "Customer",
    value: "buyer",
  },
];

const marginMoneyProviderOptions = [
  {
    label: "Vendor",
    value: "seller",
  },
  {
    label: "Customer",
    value: "buyer",
  },
  {
    label: "Poshn",
    value: "poshn",
  },
];

const delayAttributionOptions = [
  {
    label: "Vendor",
    value: "seller",
  },
  {
    label: "Customer",
    value: "buyer",
  },
  {
    label: "Agent",
    value: "agent",
  },
  {
    label: "Poshn",
    value: "poshn",
  },
];

const virtualCreditStartDateOptions = [
  {
    label: "Invoice Date",
    value: "invoice_date",
  },
  {
    label: "GRN Date",
    value: "grn_date",
  },
  {
    label: "First Vendor Payment Processed Date",
    value: "payment_processed_date",
  },
];

const agentFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "agent",
      isActive: 1,
    },
  },
});

const agentCommissionTypeOptions = [
  {
    label: "% of Invoice Amount",
    value: "percent",
  },
  {
    label: "Rate per Tons",
    value: "rate",
  },
];

const { dealSetting, dealSettingFormState } = storeToRefs(
  useDealSettingStore(),
);

const formHeading = computed(() => {
  return `${unref(dealSettingFormState).edit ? "Edit" : "Add"} Deal Setting`;
});

const {
  postDealSetting,
  putDealSetting,
  toggleDealSettingForm,
  putDealSettingStatus,
  toggleActivityLogsDrawer,
} = useDealSettingStore();

const pForm = ref(null);

const { formData, closeForm, submitForm, resetForm } = useForm({
  formModel: dealSettingFormModel(),
  entity: dealSetting,
  formRef: pForm,
  isForm: dealSettingFormState,
  post: () =>
    postDealSetting(unref(formData), {
      params: {
        showMessage: !unref(file),
      },
    }),
  put: () =>
    putDealSetting(unref(formData), {
      params: {
        showMessage: !unref(file),
      },
    }),
  toggleForm: toggleDealSettingForm,
});

const hasFormDataChanged = computed(() => {
  const keysToOmit = ["timelines"];

  return !is.deepEqual(
    omitKeys(unref(dealSetting), keysToOmit),
    omitKeys(unref(formData), keysToOmit),
  );
});

const areAgentAssociatedFieldsDisabled = computed(() => {
  return !unref(formData)?.agent?._id;
});

const handleRoceValueCheck = () => {
  if (!unref(formData).is_active && unref(isDealSettingApprovalEnabled)) {
    notify({
      message: `Validation failed: File Upload is Mandatory since approval flow is enabled`,
      type: "negative",
    });

    return;
  }

  if (!unref(formData).is_active && unref(isRoceBelowMin)) {
    notify({
      message: `Validation failed: ROCE value must not be less than ${minRocePercentage.value}%`,
      type: "negative",
    });

    return;
  }

  handleStatusUpdateConfirmation();
};

const dealSettingMenuItems = computed(() => {
  const toggleLabel = {
    true: "Disable",
    false: "Enable",
    undefined: "Disable",
  };

  return [
    {
      label: "Activity Logs",
      onClick: () => {
        toggleActivityLogsDrawer({
          level: 1,
          open: true,
          props: {
            timelines: unref(dealSetting)?.timelines?.reverse() || [],
          },
        });
      },
    },
    {
      label: toggleLabel[unref(formData).is_active],
      disable: !unref(formData)._id,
      onClick: handleRoceValueCheck,
    },
  ];
});

watchEffect(() => {
  unref(formData).buyer = props.buyer._id || props.buyer.id;

  unref(formData).seller = props.seller._id || props.seller.id;

  if (["", null, undefined].includes(unref(formData).virtual_credit_cycle)) {
    if (unref(formData).virtual_credit_cycle !== props.buyer.credit_cycle) {
      unref(formData).virtual_credit_cycle = Math.max(
        props.buyer.credit_cycle,
        1,
      );
    }
  }
});

const totalPaymentDistributionPercent = computed(() => {
  return Object.values(unref(formData).payment_distribution).reduce(
    (acc, curr) => acc + Number(curr),
    0,
  );
});

const getMaxFor = (currentKey) => {
  const max =
    100 -
    Object.entries(unref(formData).payment_distribution).reduce(
      (acc, [key, value]) => {
        if (currentKey === key) {
          return acc;
        }

        return acc + Number(value);
      },
      0,
    );

  if (currentKey === "gst_hold") {
    return max > 12 ? 12 : max;
  }

  return max;
};

const onUpdateMarginMoneyProvider = () => {
  if (unref(formData).margin_money_provider === "buyer") {
    unref(formData).is_gst_amount_hold = false;

    unref(formData).payment_distribution.gst_hold = 0;
  }
};

const onUpdateGstAmountHold = (gstAmountHold) => {
  if (!gstAmountHold) {
    unref(formData).payment_distribution.gst_hold = 0;
  }
};

const onUpdateAgent = (agent) => {
  const { _id = null, name = "" } = agent || {};

  unref(formData).agent = {
    _id,
    name,
    commission_type: "percent",
    commission_percent: 0,
    commission_rate: 0,
  };

  updateRoceValue();
};

const onUpdateAgentCommissionType = () => {
  unref(formData).agent.commission_percent = 0;

  unref(formData).agent.commission_rate = 0;

  updateRoceValue();
};

const maxCommissionValue = computed(() => {
  return unref(formData).agent.commission_type === "percent" ? 100 : undefined;
});

const {
  isFetchingMinRocePercentage,
  updateRoceValue,
  minRocePercentage,
  getMinDelayInterest,
  isRoceBelowMin,
  errorMsgForRoceField,
  delayInterestProps,
  isDealSettingApprovalEnabled,
} = useRoce({
  formData,
});

const updateFormData = (value, key) => {
  const target = unref(formData);

  if (key.includes(".")) {
    setProperty(target, key, value);
  } else {
    target[key] = value;
  }

  updateRoceValue();
};

const disableCta = computed(() => {
  if (unref(isDealSettingApprovalEnabled)) {
    if (unref(file)) {
      return false;
    } else if (unref(formData)?.files?.length) {
      return !unref(hasFormDataChanged);
    }

    return !unref(hasFormDataChanged);
  }

  return false;
});

const isPrimaryCtaDisabled = computed(() => {
  return (
    unref(totalPaymentDistributionPercent) < 100 ||
    unref(isRoceBelowMin) ||
    unref(delayInterestProps).error ||
    unref(disableCta)
  );
});

const resetPostGrnDays = (grn) => {
  if (grn === 0) {
    unref(formData).grn_payment_delay_days = 0;
  }
};

const submitButton = computed(() => {
  const commonProps = {
    disable: unref(isPrimaryCtaDisabled),
    loading: unref(isLoading),
  };

  if (unref(isDealSettingApprovalEnabled)) {
    return {
      ...commonProps,
      label: "Send For Approval",
    };
  }

  return {
    ...commonProps,
  };
});

const { file, uploadFile, resetFile } = useFile(formData);

const isLoading = ref(false);

const confirmSubmission = async () => {
  if (unref(isDealSettingApprovalEnabled)) {
    const seller = unref(props)?.seller;
    const account_owner_name =
      seller?.account_owner_info?.name ||
      seller?.othersInfo?.account_owner?.name;

    const shop_name = seller?.shop_name;

    const confirmationMessage = account_owner_name
      ? `Do you want to send this deal for approval to ${account_owner_name}?`
      : `There is no account owner mapped to ${shop_name} Do you still want to send this deal for approval?`;

    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        labelClass: "text-center q-ma-xl rounded-borders",
        text: confirmationMessage,
        cancelLabel: "Cancel",
        submitLabel: "Confirm",
        onConfirm: () => onSubmit(),
      },
    });

    return;
  }

  await onSubmit();
};

const onSubmit = async () => {
  try {
    isLoading.value = true;

    const promises = [submitForm(false)];

    if (unref(formData)._id && unref(file)) {
      promises.push(
        uploadFile({
          _id: unref(formData)._id,
          type: "Deal Terms",
          showMessage: true,
        }),
      );
    }

    const [submission] = await Promise.allSettled(promises);

    if (unref(file) && !unref(formData)._id) {
      await uploadFile({
        ...submission?.value?.data?.data,
        type: "Deal Terms",
        showMessage: true,
      });
    }

    closeForm(true);
  } catch (error) {
    console.error("Error in onSubmit:", error);
  } finally {
    isLoading.value = false;
  }
};

const showFileUpload = computed(() => {
  return unref(isDealSettingApprovalEnabled);
});

const computedStatus = computed(() => {
  if (unref(isDealSettingApprovalEnabled) && !unref(formData).is_active) {
    const rejectedStatus = "rejected";

    const approvers = unref(formData)?.approvers || [];

    if (
      approvers.findIndex((approver) => approver.status_cd === rejectedStatus) >
      -1
    ) {
      return {
        label: useChangeCase("rejected", "capitalCase").value,
        type: "rejected",
      };
    }

    return {
      label: useChangeCase("awaiting_approval", "capitalCase").value,
      type: "awaiting_approval",
    };
  }

  return {
    label: statusLabel[unref(formData).is_active],
    type: labelType[unref(formData).is_active],
  };
});

const toolTipContent = computed(() => {
  if (!unref(formData).is_active && unref(isDealSettingApprovalEnabled)) {
    const approvers = unref(formData)?.approvers || [];

    const rejectedApprovers = approvers.filter(
      (approver) => approver.status_cd === "rejected",
    );

    if (rejectedApprovers.length > 0) {
      const rejectorsNameWithReason = rejectedApprovers
        .map((approver) => ({
          textToShow: `${approver.name}(Reason: ${approver.reason})`,
        }))
        .map((approver) => approver.textToShow)
        .join(", ");

      return rejectorsNameWithReason;
    }

    const approversName = approvers.map((approver) => approver.name).join(", ");

    return approversName;
  }

  return null;
});

const handleClose = async () => {
  resetFile();

  closeForm();
};

const handleReset = () => {
  resetFile();

  resetForm();
};
</script>
